import React, { useEffect, useState } from 'react';
import { Trash2, ChevronDown, ChevronUp, ArrowRightLeft } from 'lucide-react';
import PercentageInput from '../../../../components/ui/inputs/PercentageInput';
import useHighlightOnAdd from '../../../../components/hooks/useHighlightOnAdd';

const getAssetTitle = (asset) => {
  return (
    asset.assetDisplayName ||
    asset.assetName ||
    asset.displayName ||
    asset.name ||
    asset.assetAlias ||
    asset.alias ||
    asset.assetTicker ||
    asset.ticker ||
    asset.assetSymbolId ||
    asset.symbolId ||
    '--'
  );
};

const getAssetTicker = (asset) => {
  return asset.assetTicker || asset.ticker || asset.assetSymbolId || asset.symbolId || '--';
};

const getAssetSymbolId = (asset) => {
  return asset.assetSymbolId || asset.symbolId || '--';
};

const getAssetCurrency = (asset) => {
  return asset.assetCurrency || asset.currency || '--';
};

const getAssetExchange = (asset) => {
  return asset.assetExchange || asset.exchange || '--';
};

const AssetRow = ({
  asset,
  idx,
  gridColumns,
  highlightId,
  onChangeAllocation,
  onRemoveAsset,
  onReplaceAsset,
  onRequestOpen,
}) => {
  const isBlueRow = idx % 2 !== 1;

  const assetTitle = getAssetTitle(asset);
  const ticker = getAssetTicker(asset);
  const symbolId = getAssetSymbolId(asset);
  const currency = getAssetCurrency(asset);
  const exchange = getAssetExchange(asset);

  const shouldHighlight = asset._highlightId != null && asset._highlightId === highlightId;
  const { ref, isHighlighted } = useHighlightOnAdd(shouldHighlight);
  const rowBackground = isHighlighted ? 'bg-yellowMedium' : isBlueRow ? 'bg-blueLight' : 'bg-transparent';

  return (
    <div
      ref={ref}
      className={`${rowBackground} rounded-xl transition-colors`}
      style={{ transitionDuration: isHighlighted ? '0ms' : '2000ms' }}
    >
      <div className="grid items-center gap-4 px-3 py-3" style={{ gridTemplateColumns: gridColumns }}>
        <div className="min-w-0 flex flex-col gap-0.5 font-semibold text-blueDarker" title={assetTitle}>
          <span className="min-w-0 truncate">{assetTitle}</span>

          {asset.assetAlias && asset.assetAlias !== assetTitle && (
            <span className="text-xs font-normal italic text-blueSteel truncate">{asset.assetAlias}</span>
          )}
        </div>

        <div className="truncate text-blueDarker" title={ticker}>
          {ticker}
        </div>

        <div className="truncate text-blueDarker" title={symbolId}>
          {symbolId}
        </div>

        <div className="truncate text-blueDarker" title={currency}>
          {currency}
        </div>

        <div className="truncate text-blueDarker" title={exchange}>
          {exchange}
        </div>

        <div className="flex justify-end gap-2">
          <PercentageInput
            name={`allocation-${asset.originalIndex}`}
            value={asset.allocationPercentage}
            onChange={(e) => onChangeAllocation(asset.originalIndex, e.target.value)}
            onFocus={onRequestOpen}
            py="py-2"
          />

          <button
            type="button"
            tabIndex={-1}
            onClick={() => onReplaceAsset(asset, asset.originalIndex)}
            className="bg-tealMedium hover:opacity-90 transition-opacity text-white font-semibold rounded-xl px-4 pt-3 pb-2 text-xs border-r-2 border-b-2 border-tealBase"
            title="Substituir ativo"
          >
            <ArrowRightLeft size={18} className="inline-block" strokeWidth={1.5} />
          </button>

          <button
            type="button"
            tabIndex={-1}
            className="bg-redSoft hover:opacity-90 transition-opacity text-white font-semibold rounded-xl px-4 pt-2 pb-2 text-xs border-r-2 border-b-2 border-redDeep"
            onClick={() => onRemoveAsset(asset.originalIndex)}
            title="Remover ativo"
          >
            <Trash2 size={16} className="inline-block" strokeWidth={2} />
          </button>
        </div>
      </div>
    </div>
  );
};

const InternationalAssetsAccordion = ({
  assets,
  highlightId,
  onRemoveAsset,
  onChangeAllocation,
  onReplaceAsset,
  defaultOpen = true,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const hasHighlightedAsset = highlightId != null && assets.some((asset) => asset._highlightId === highlightId);

  useEffect(() => {
    if (hasHighlightedAsset) setIsOpen(true);
  }, [hasHighlightedAsset]);

  const gridColumns = 'minmax(220px, 2fr) 110px minmax(150px, 1.2fr) 90px 110px 260px';

  const sumPercentages = assets.reduce((sum, asset) => sum + parseFloat(asset.allocationPercentage || 0), 0);

  return (
    <div className="flex flex-col gap-3">
      <div
        className="grid items-center text-base bg-tealMedium text-white font-semibold p-3 rounded-xl cursor-pointer select-none transition-colors hover:bg-tealDark shadow-sm"
        onClick={() => setIsOpen(!isOpen)}
        style={{ gridTemplateColumns: gridColumns }}
      >
        <span className="flex items-center gap-2">Ativos Internacionais</span>
        <span></span>
        <span></span>
        <span></span>
        <span></span>

        <span className="flex justify-between items-center">
          <span className="bg-white text-tealMedium px-2 py-0.5 rounded-full text-sm font-bold">
            {sumPercentages.toFixed(1).replace('.', ',')}%
          </span>

          {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </span>
      </div>

      <div
        className={`grid transition-all duration-300 ease-in-out ${isOpen ? 'opacity-100' : 'opacity-0'}`}
        style={{ gridTemplateRows: isOpen ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden">
          <div className="pt-1 pb-2">
            {assets.length > 0 ? (
              <>
                <div className="px-2">
                  <div
                    className="grid items-center gap-4 text-blueDarker text-base font-semibold px-1"
                    style={{ gridTemplateColumns: gridColumns }}
                  >
                    <div className="truncate">Ativo</div>
                    <div className="truncate">Ticker</div>
                    <div className="truncate">Symbol ID</div>
                    <div className="truncate">Moeda</div>
                    <div className="truncate">Exchange</div>
                    <div></div>
                  </div>
                </div>

                <div className="space-y-1 mt-2">
                  {assets.map((asset, idx) => (
                    <AssetRow
                      key={asset.id || `new-${asset.originalIndex}`}
                      asset={asset}
                      idx={idx}
                      gridColumns={gridColumns}
                      highlightId={highlightId}
                      onChangeAllocation={onChangeAllocation}
                      onRemoveAsset={onRemoveAsset}
                      onReplaceAsset={onReplaceAsset}
                      onRequestOpen={() => setIsOpen(true)}
                    />
                  ))}
                </div>
              </>
            ) : (
              <div className="bg-blueLight rounded-xl px-5 py-5 text-center">
                <h3 className="text-base font-semibold text-blueDarker">Nenhum ativo internacional adicionado</h3>

                <p className="text-sm text-blueSteel mt-1">
                  Adicione ativos para montar a composição sugerida desta carteira.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const AccountBalanceAccordion = ({ percentage, onChange, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const val = parseFloat(percentage || 0);

  const gridColumns = 'minmax(220px, 2fr) 110px minmax(150px, 1.2fr) 90px 110px 260px';

  return (
    <div className="flex flex-col gap-3">
      <div
        className="grid items-center text-base bg-tealMedium text-white font-semibold p-3 rounded-xl cursor-pointer select-none transition-colors hover:bg-tealDark shadow-sm"
        onClick={() => setIsOpen(!isOpen)}
        style={{ gridTemplateColumns: gridColumns }}
      >
        <span className="flex items-center gap-2">Saldo em Conta</span>
        <span></span>
        <span></span>
        <span></span>
        <span></span>

        <span className="flex justify-between items-center gap-4">
          <span className="bg-white text-tealMedium px-2 py-0.5 rounded-full text-sm font-bold">
            {val.toFixed(1).replace('.', ',')}%
          </span>

          {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </span>
      </div>

      <div
        className={`grid transition-all duration-300 ease-in-out ${isOpen ? 'opacity-100' : 'opacity-0'}`}
        style={{ gridTemplateRows: isOpen ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden">
          <div className="pt-1 pb-2">
            <div className="bg-blueLight rounded-xl transition-colors">
              <div className="flex items-center justify-between px-3 py-3">
                <div>
                  <div className="font-semibold text-blueDarker">Percentual em saldo na conta offshore</div>
                  <div className="text-sm italic text-blueSteel">
                    Use este campo para definir a parte da carteira que não será alocada em ativos internacionais.
                  </div>
                </div>

                <div className="w-32">
                  <PercentageInput
                    name="accountBalancePercentage"
                    value={percentage}
                    onChange={(e) => onChange(e.target.value)}
                    inputClassName="py-2"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const OffshoreWalletAssets = ({
  assetsAttributes = [],
  highlightId,
  onRemoveAsset,
  onChangeAllocation,
  onReplaceAsset,
  accountBalancePercentage,
  onChangeAccountBalance,
}) => {
  const visibleAssets = (assetsAttributes || [])
    .map((asset, index) => ({ ...asset, originalIndex: index }))
    .filter((asset) => !asset._destroy)
    .sort((a, b) => getAssetTitle(a).toLowerCase().localeCompare(getAssetTitle(b).toLowerCase(), 'pt-BR'));

  const commonProps = {
    highlightId,
    onRemoveAsset,
    onChangeAllocation,
    onReplaceAsset,
  };

  return (
    <div className="mt-4 flex flex-col gap-5 pb-6 w-full">
      <InternationalAssetsAccordion title="Internacionais" assets={visibleAssets} {...commonProps} />
      <AccountBalanceAccordion percentage={accountBalancePercentage} onChange={onChangeAccountBalance} />
    </div>
  );
};

export default OffshoreWalletAssets;
